import React, { useCallback, useEffect, useState, useRef } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableFooter,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Skeleton,
  useTheme,
  useMediaQuery,
  Popover,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  InputLabel,
  TextField,
  MenuItem
} from '@mui/material';
import Paper from '@mui/material/Paper';
import useSelectedGroup from 'src/hooks/useSelectedGroup';
import { useTranslation } from 'react-i18next';
import useAuth from 'src/hooks/useAuth';
import GetSubAccountAlerts from 'src/api/GetSubAccountAlerts';
import TablePaginationComp from 'src/components/TablePaginationComp';
import DeleteAlert from 'src/api/DeleteAlert';
import { useSnackbar } from 'notistack';
import { Link as RouterLink } from 'react-router-dom';
import { ChartBarBig } from 'lucide-react-new';
import { ChevronsUp as ScrollArrowsUp, ChevronsDown as ScrollArrowsDown } from 'lucide-react';
import CreateAlertButton from 'src/components/CreateAlertButton.js'
import { TableSortControls } from 'src/components/TableSortControls';
import { applySorting, getComparator } from 'src/utils/filterAndSortData';
import { SquarePen, ChevronDown, X } from 'lucide-react-new';
import EmailChipsInput from 'src/components/EmailChipsInput.js'
import { hashrateUnitOptions, convertToGHValue } from 'src/utils/alerts';
import UpdateSubAccountAlert from 'src/api/UpdateSubAccountAlert';
import ConfirmationDialog from 'src/components/ConfirmationDialog';

const useStyles = makeStyles()(theme => ({
  table: {
    minWidth: 650
  },
  metalTableHeaderRow: {
    height: '40px',
    backgroundColor: 'var(--color-surface-raised)'
  },
  metalTableDataRow: {
    height: '50px',
    backgroundColor: 'var(--color-surface)'
  },
  metalAction: {
    backgroundColor: 'var(--accent-9)',
    color: 'var(--accent-9-contrast)',
    '&:hover, &.Mui-focusVisible': {
      backgroundColor: 'var(--accent-9)'
    }
  },
  alertsBtn: {
    color: '#e6e5e8 !important',
    fontSize: '16px',
    textTransform: 'capitalize',
    borderRadius: "var(--radius-md, 8px)",
    border: "1px solid var(--Component-colors-Components-Buttons-Secondary-color-button-secondary-color-border, #364152)",
    background: "var(--Component-colors-Components-Buttons-Secondary-color-button-secondary-color-bg, #121926)",
    boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
    whiteSpace: 'nowrap',
    '&:hover' : {
      textDecoration: "none !important"
    },
  },
  cardHeader: {
    '& .MuiCardHeader-action':{
      margin: 0
    }
  },
  scrollButtonsContainer: {
    display: 'flex',
    flexDirection: 'column'
  },
  hideScrollButton: {
    display: 'none'
  },
  topScrollButton: {
    display: 'flex',
    alignSelf: 'center',
    position: 'absolute',
    top: '72px',
    height: '50px',
    width: '50px',
    backgroundColor: 'var(--color-accent)',
    outline: 'none',
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1001
  },
  bottomScrollButton: {
    position: 'absolute',
    display: 'flex',
    alignSelf: 'center',
    bottom: '12px',
    height: '50px',
    width: '50px',
    backgroundColor: 'var(--color-accent)',
    outline: 'none',
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1001
  },
  selectLabel: {
    color: "var(--colors-text-text-secondary-700, #CDD5DF)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
    marginTop: "8px",
    marginBottom: "8px"
  },
  selectField: {
    borderRadius: "var(--radius-md, 8px)",
    border: "1px solid var(--Colors-Border-border-primary, #364152)",
    background: "var(--Colors-Background-bg-primary, #0D121C)",
    boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
    marginBottom: "8px"
  },
  splitFieldLeft: {
    width: "75%",
    borderTopLeftRadius: "var(--radius-md, 8px)",
    borderBottomLeftRadius: "var(--radius-md, 8px)",
    border: "1px solid var(--Colors-Border-border-primary, #364152)",
    borderRight: "none",
    background: "var(--Colors-Background-bg-primary, #0D121C)",
    boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
    marginBottom: "8px",
    '& .MuiOutlinedInput-notchedOutline':{

      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    }
  },
  splitFieldRight:{
    width: "25%",
    borderTopRightRadius: "var(--radius-md, 8px)",
    borderBottomRightRadius: "var(--radius-md, 8px)",
    border: "1px solid var(--Colors-Border-border-primary, #364152)",
    borderLeft: "none",
    background: "var(--Colors-Background-bg-primary, #0D121C)",
    boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
    marginBottom: "8px",
    '& .MuiOutlinedInput-notchedOutline':{

      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    }
  },
  alertsBtn: {
    '&:hover, &.Mui-focusVisible': {
      textDecoration: 'none !important'
    }
  },
  buttonBase: {
    display: "flex",
    padding: "10px var(--spacing-xl, 16px)",
    justifyContent: "center",
    alignItems: "center",
    gap: "var(--spacing-sm, 6px)",
    flex: "1 0 0",
    borderRadius: "var(--radius-md, 8px)",
    border: "1px solid var(--Component-colors-Components-Buttons-Secondary-button-secondary-border, #333741)",
    background: "var(--Component-colors-Components-Buttons-Secondary-button-secondary-bg, #161B26)",
    boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
    color: "#CECFD2",
    textTransform: "capitalize",
    fontSize: "16px",
    fontWeight: "600",
    lineHeight: "24px"
  },
  buttonAction: {
    backgroundColor: '#00B161',
    marginLeft: "10px",
    color: 'var(--accent-9-contrast)',
    '&:hover, &.Mui-focusVisible': {
      backgroundColor: 'var(--accent-9)'
    }
  },
  actionIcon: {
    marginRight: theme.spacing(1)
  },
}));

const ManageAlertsTable = props => {
  const { selectedGroup } = useSelectedGroup();
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { coin, update, setUpdate, setNoData, loading, setLoading } = props;
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [subAccountOrder, setSubAccountOrder] = useState('asc');
  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);
  const { getAccessToken, loggedInUser } = useAuth();
  const theme = useTheme();
  const mediumDevice = useMediaQuery(theme.breakpoints.down('md'));
  const smallerDevice = useMediaQuery(theme.breakpoints.down(450));

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAlertId, setSelectedAlertId] = useState(null);
  const [selectedSubAccount, setSelectedSubAccount] = useState(null);
  const [selectedAlertGroup, setSelectedAlertGroup] = useState('');
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
  const [editAlertModalOpen, setEditAlertModalOpen] = useState(false);
  const [editAlertLoading, setEditAlertLoading] = useState(false);
  const [hashrateThreshold, setHashrateThreshold] = useState('');
  const [ hashrateUnit, setHashrateUnit ] = useState('');
  const [activeWorkersThreshold, setActiveWorkersThreshold] = useState('');
  const [ emailsTextArea, setEmailsTextArea ] = useState('');
  const [ emailList, setEmailList ] = useState([]);
  const [ emailListError, setEmailListError ] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const [usersOrderMobile, setUsersOrderMobile] = useState('asc')
  const [usersOrderByMobile, setUsersOrderByMobile] = useState('subAccountName')

  const topElementRef = useRef(null);
  const lastElementRef = useRef(null);

  const handleScrollTop = e => {
    topElementRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  const handleScrollBottom = e => {
    lastElementRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  const useVisibility = (button, ref) => {
    const offset = 700;
    const [isVisible, setIsVisible] = useState(true || !loading);

    const onScroll = () => {
      if (!ref || !ref?.current) {
        setIsVisible(false);
        return;
      }
      const top = ref.current.getBoundingClientRect().top;
      setIsVisible(
        (button === 'top' ? top + offset >= 0 : top < offset) &&
          top - offset <= window.innerHeight
      );
    };

    useEffect(() => {
      document.addEventListener('scroll', _.throttle(onScroll, 1000), true);
      return () =>
        document.removeEventListener(
          'scroll',
          _.throttle(onScroll, 1000),
          true
        );
    });

    return isVisible;
  };

  let topIsVisible = useVisibility('top', mediumDevice ? topElementRef : null);
  let bottomIsVisible = useVisibility(
    'bottom',
    mediumDevice ? lastElementRef : null
  );

  const handleSubAccountColumnSort = () => {
    const isAsc = subAccountOrder === 'asc';
    setSubAccountOrder(isAsc ? 'desc' : 'asc');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setAnchorEl(null);
    setSelectedAlertId(null);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setAnchorEl(null);
    setSelectedAlertId(null);
  };

  const handleMobileUsersCardsSort = (tableColumn, columnOrder) => {
    setUsersOrderMobile(columnOrder)
    setUsersOrderByMobile(tableColumn)
  }

  const handleNotifyClick = (e, alertId) => {
    setAnchorEl(e.currentTarget);
    setSelectedAlertId(alertId);
  };

  const handleNotifyClose = () => {
    setAnchorEl(null);
    setSelectedAlertId(null);
  };

  const handleDeleteConfirmDialogClose = () => {
    setDeleteConfirmDialogOpen(false);
    setSelectedAlertId(null);
    setSelectedSubAccount(null);
  }

  const handleDeleteClick = (subAccount, alertId) => {
    setSelectedAlertId(alertId);
    setSelectedSubAccount(subAccount);
    setDeleteConfirmDialogOpen(true);
  }

  const handleEditClick = ({alertId, subAccountName, groupName, hashrateThreshold, activeWorkersThreshold, emailList}) => {
    const hashrateValue = hashrateThreshold?.length ? hashrateThreshold?.split(' ')[0] : 0;
    const hashrateUnit = hashrateThreshold?.length ? `${hashrateThreshold?.split(' ')[1]}/s` : 'PH/s';
    setSelectedAlertId(alertId);
    setSelectedSubAccount(subAccountName);
    setSelectedAlertGroup(groupName);
    setHashrateThreshold(hashrateValue);
    setHashrateUnit(hashrateUnit);
    setActiveWorkersThreshold(activeWorkersThreshold.toLocaleString());
    handleSelectedEmails(emailList);
    setEditAlertModalOpen(true);
  }
  const handleEditModalClose = () => {
    setEditAlertModalOpen(false);
    setSelectedAlertId(null);
    setSelectedSubAccount(null);
    setSelectedAlertGroup(null);
    setHashrateThreshold(null);
    setHashrateUnit(null);
    setActiveWorkersThreshold(null);
    handleSelectedEmails([]);
  }

  const handleHashrateThresholdChange = (event) => {
    setHashrateThreshold(event.target.value)
  }

  const handleHashrateUnitChange= (event) => {
    setHashrateUnit(event.target.value)
  }

  const handleActiveWorkersThresholdChange = (event) => {
    setActiveWorkersThreshold(event.target.value)
  }

  function handleSelectedEmails(emails) {
    setEmailList(emails);
  }


  const handleEditAlert = async() => {
    setEditAlertLoading(true)
    const { tokenAvailable, accessToken } = await getAccessToken();
    if (!tokenAvailable) {
      console.log('Error: ' + accessToken);
    }
    const convertedGHValue = convertToGHValue(hashrateUnit, removeCommas(hashrateThreshold));
    const activeWorkersFormatted = removeCommas(activeWorkersThreshold);

    const response = await UpdateSubAccountAlert(coin, selectedAlertId, selectedSubAccount, convertedGHValue, activeWorkersFormatted, emailList, accessToken);
    setEditAlertLoading(false)
    if (response.success){    
      enqueueSnackbar(t('alertEditedSuccessfully'), {
        variant: 'success',
      });
    }
    else{
      enqueueSnackbar(response.data, {
        variant: 'error',
      });
    }
    handleEditModalClose();
    fetchData()
  }

  const removeCommas = (val) => {
    if (val.toString().includes(',')) {
      const formattedValue = val.toString().trim().replaceAll(',', '');
      return Number(formattedValue);
    } 
    return Number(val);
  }
    
  const handleDeleteAlert = async() => {
    setLoading(true)
    const { tokenAvailable, accessToken }= await getAccessToken();
    if (!tokenAvailable) {
      console.log('Error: ' + accessToken);
    }
    const response = await DeleteAlert(coin, selectedSubAccount, selectedAlertId, accessToken)
    setLoading(false)
    if (response.success){    
      enqueueSnackbar(t('alertDeletedSuccessfully'), {
        variant: 'success',
      });
    }
    else{
      enqueueSnackbar(response.data, {
        variant: 'error',
      });
    }
    handleDeleteConfirmDialogClose();
    fetchData()
  }

  const renderTableSkeleton = () => {
    return (
      <TableBody>
        <TableRow className={classes.metalTableDataRow}>
          <TableCell align="left" aligncomponent="th" scope="row">
            <Skeleton variant="rounded" height={20} width={100} />
          </TableCell>
          {loggedInUser?.groups?.length > 1 && (
            <TableCell align={'left'}>
              <Skeleton variant="rounded" height={20} width={100} />
            </TableCell>
          )}
          <TableCell align={'left'}>
            <Skeleton variant="rounded" height={20} width={100} />
          </TableCell>
          <TableCell align={'left'}>
            <Skeleton variant="rounded" height={20} width={100} />
          </TableCell>
          <TableCell align={'left'}>
            <Skeleton variant="rounded" height={20} width={100} />
          </TableCell>
          <TableCell>
            <Skeleton variant="rounded" height={20} width={100} />
          </TableCell>
        </TableRow>
      </TableBody>
    );
  };

  const renderAlertsTable = () => {
    return (
      <TableBody>
        {(rowsPerPage > 0
          ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          : rows
        ).map(row => (
          <TableRow className={classes.metalTableDataRow} key={row.alertId}>
            <TableCell
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
              align="left"
              aligncomponent="th"
              scope="row"
            >
              {row.subAccountName}
            </TableCell>
            {loggedInUser?.groups?.length > 1 && (
              <TableCell
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: '16px',
                  whiteSpace: 'nowrap'
                }}
                align={'left'}
              >
                {row.groupName}
              </TableCell>
            )}
            <TableCell
              style={{ fontSize: '16px', whiteSpace: 'nowrap' }}
              align={'left'}
            >
              {Object.keys(row.alertType)?.map((key, index) => {
                return row.alertType[key] ? (
                  <div
                    key={index}
                    className="m-chip"
                    data-color-status={
                      key === 'hashrate' ? 'info' : 'highlight'
                    }
                    style={{ textTransform: 'capitalize', marginRight: 5 }}
                  >
                    {t(`${key}`)}
                  </div>
                ) : null;
              })}
            </TableCell>
            <TableCell
              style={{
                fontSize: '16px',
                color: 'var(--color-text-warning)',
                whiteSpace: 'nowrap'
              }}
              align={'left'}
            >
              {row.hashrateThreshold !== null && (
                <div
                  className="m-chip"
                  data-color-status="info"
                  style={{
                    textTransform: 'capitalize',
                    marginRight: 5,
                    display: 'inline-flex'
                  }}
                >
                  {row.hashrateThreshold}
                </div>
              )}
              {row.activeWorkersThreshold > 0 && (
                <div
                  className="m-chip"
                  data-color-status="highlight"
                  style={{
                    textTransform: 'capitalize',
                    marginRight: 5,
                    display: 'inline-flex'
                  }}
                >
                  {row.activeWorkersThreshold.toLocaleString()}{' '}
                  {row.activeWorkersThreshold === 1 ? 'Worker' : 'Workers'}
                </div>
              )}
            </TableCell>
            <TableCell>
              <div
                className="m-chip"
                style={{
                  textTransform: 'capitalize',
                  background: 'transparent',
                  border: '1px solid var(--color-border-default)',
                  cursor: 'pointer'
                }}
                onClick={e => handleNotifyClick(e, row.alertId)}
              >
                {row.emailList?.length}{' '}
                {row.emailList?.length === 1 ? 'Email' : 'Emails'}
              </div>
              <Popover
                id={row.alertId}
                open={selectedAlertId === row.alertId && anchorEl !== null}
                anchorEl={anchorEl}
                onClose={handleNotifyClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center'
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center'
                }}
                slotProps={{
                  paper: {
                    style: {
                      backgroundColor: '#121926',
                      marginTop: 10
                    }
                  }
                }}
              >
                <div style={{ padding: 14, width: 375 }}>
                  <Typography style={{ paddingBottom: 10, fontWeight: 600 }}>
                    People notified by this alert
                  </Typography>
                  {row.emailList?.map((email, index) => {
                    return (
                      <p key={index} className="m-text">
                        {email}
                      </p>
                    );
                  })}
                </div>
              </Popover>
            </TableCell>
            <TableCell>
              <div style={{ display: 'flex', gap: 'var(--spacing-lg, 12px)' }}>
                <span
                  style={{ cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
                  onClick={() =>
                    handleDeleteClick(row.subAccountName, row.alertId)
                  }
                >
                  Delete
                </span>
                <span
                  style={{ cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
                  onClick={() => handleEditClick(row)}
                >
                  Edit
                </span>
              </div>
            </TableCell>
          </TableRow>
        ))}
        {emptyRows > 0 && (
          <TableRow style={{ height: emptyRows }}>
            <TableCell colSpan={6} />
          </TableRow>
        )}
      </TableBody>
    );
  };

  const fetchData = useCallback(async () => {
    const { tokenAvailable, accessToken } = await getAccessToken();
    setLoading(true);

    if (!tokenAvailable) {
      return;
    }
    const { success, data } = await GetSubAccountAlerts(
      accessToken,
      coin,
      subAccountOrder === 'asc' ? 'ascSubAccount' : 'descSubAccount',
      selectedGroup,
      false
    );
    if (success) {
      setRows(data);
      if(data.length) {
        setNoData(false)
      }
    } else {
      setRows([]);
      setError(data);
    }
    setLoading(false);
  }, [getAccessToken, coin, selectedGroup, subAccountOrder]);

  useEffect(() => {
    setPage(0);
  }, [selectedGroup]);

  useEffect(() => {
    fetchData();
  }, [fetchData, update]);

  const renderCards = () => {      
    return (
      <>
        {!!rows.length &&
          applySorting(rows, getComparator(usersOrderMobile, usersOrderByMobile)).map(
            (card, index) => {
              return (
                <Card ref={rows.length === index + 1 ? lastElementRef : null} key={card.alertId} variant='outlined' sx={{ marginTop: "10px", backgroundColor: "var(--neutral-2)", borderColor: "var(--color-border)"}}>
                  <CardHeader
                    className={classes.cardHeader}
                    sx={{paddingBottom: "0", marginTop: 0}}
                    title={card.subAccountName}
                    subheader={card.groupName}
                    action={
                      <div style={{ display: 'flex', columnGap: 15 }}>
                        <span style={{ 
                          cursor: 'pointer', 
                          fontSize: 14, 
                          fontWeight: 600,
                          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                          fontWeight: "500",
                          fontSize: "0.875rem",
                          lineHeight: "1.43",
                          letterSpacing: "0.01071em",
                          display: "flex",
                          justifyContent: "flex-start",
                          textAlign: "left",
                          padding: "4px 8px",
                          color: "#e6e5e8",
                          border: "1px solid #e6e5e8",
                          borderRadius: "4px",
                          margin: "0px !important"
                        }} 
                        onClick={() => handleDeleteClick(card.subAccountName, card.alertId)}>Delete</span>
                        <span style={{ 
                          cursor: 'pointer', 
                          fontSize: 14, 
                          fontWeight: 600,
                          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                          fontWeight: "500",
                          fontSize: "0.875rem",
                          lineHeight: "1.43",
                          letterSpacing: "0.01071em",
                          display: "flex",
                          justifyContent: "flex-start",
                          textAlign: "left",
                          padding: "4px 8px",
                          color: "#e6e5e8",
                          border: "1px solid #e6e5e8",
                          borderRadius: "4px",
                          margin: "0px !important"
                        }} 
                        onClick={() => handleEditClick(card)}>Edit</span>
                      </div>
                      
                    }
                  />
                  <CardContent>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell align="left" sx={{paddingLeft: 0, paddingRight: 0, width: "50%"}}>
                            <Typography variant='h6'>{t('alertType')}</Typography>
                          </TableCell>
                          {Object.keys(card.alertType)?.filter(h => h === "hashrate").map((key, index) => {
                            return card.alertType[key] ? (
                              <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                                <div
                                  key={index}
                                  className="m-chip"
                                  data-color-status={'info'}
                                  style={{ textTransform: 'capitalize', marginRight: 5 }}
                                >
                                  {t(`${key}`)}
                                </div>
                              </TableCell>
                            ) : null;
                          })} 
                          {Object.keys(card.alertType)?.filter(h => h === "workers").map((key, index) => {
                            return card.alertType[key] ? (
                              <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                                <div
                                  key={index}
                                  className="m-chip"
                                  data-color-status={'highlight'}
                                  style={{ textTransform: 'capitalize', marginRight: 5 }}
                                >
                                  {t(`${key}`)}
                                </div>
                              </TableCell>
                            ) : null;
                          })}                       
                        </TableRow>
                        <TableRow>
                          <TableCell align="left" sx={{paddingLeft: 0, paddingRight: 0}}>
                            <Typography variant='h6'>{t('threshold')}</Typography>
                          </TableCell>
                          {card.hashrateThreshold !== null && (
                            <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                              <div
                                className="m-chip"
                                data-color-status="info"
                                style={{
                                  textTransform: 'capitalize',
                                  marginRight: 5,
                                  display: 'inline-flex'
                                }}
                              >
                                {card.hashrateThreshold}
                              </div>
                            </TableCell>
                          )}
                          {card.activeWorkersThreshold > 0 && (
                            <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                              <div
                                className="m-chip"
                                data-color-status="highlight"
                                style={{
                                  textTransform: 'capitalize',
                                  marginRight: 5,
                                  display: 'inline-flex'
                                }}
                              >
                                {`${card.activeWorkersThreshold.toLocaleString()} ${smallerDevice ? "" : card.activeWorkersThreshold === 1 ? 'Worker' : 'Workers'}`}
                              </div>
                            </TableCell>
                          )} 
                        </TableRow>
                        <TableRow>
                          <TableCell align="left" sx={{paddingLeft: 0, paddingRight: 0}}>
                            <Typography variant='h6'>{t('notifying')}</Typography>
                          </TableCell>
                          { card.alertType?.hashrate && card.alertType?.workers 
                            ? <TableCell></TableCell>
                            : <TableCell align="right" sx={{flex: 1, paddingLeft: 0, paddingRight: 0}}>
                                <div
                                  className="m-chip"
                                  style={{
                                    textTransform: 'capitalize',
                                    background: 'transparent',
                                    border: '1px solid var(--color-border-default)',
                                    cursor: 'pointer'
                                  }}
                                  onClick={e => handleNotifyClick(e, card.alertId)}
                                >
                                  {card.emailList?.length}{' '}
                                  {card.emailList?.length === 1 ? 'Email' : 'Emails'}
                                </div>
                                <Popover
                                  id={card.alertId}
                                  open={selectedAlertId === card.alertId && anchorEl !== null}
                                  anchorEl={anchorEl}
                                  onClose={handleNotifyClose}
                                  anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'center'
                                  }}
                                  transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'center'
                                  }}
                                  slotProps={{
                                    paper: {
                                      style: {
                                        backgroundColor: '#121926',
                                        margin: 10
                                      }
                                    }
                                  }}
                                >
                                  <div style={{ padding: 14, width: 375 }}>
                                    <Typography style={{ paddingBottom: 10, fontWeight: 600 }}>
                                      People notified by this alert
                                    </Typography>
                                    {card.emailList?.map((email, index) => {
                                      return <p key={index} className="m-text">{email}</p>;
                                    })}
                                  </div>
                                </Popover>
                              </TableCell>
                          }
                          { card.alertType?.hashrate && card.alertType?.workers &&
                            <TableCell align="right" sx={{flex: 1, paddingLeft: 0, paddingRight: 0}}>
                              <div
                                className="m-chip"
                                style={{
                                  textTransform: 'capitalize',
                                  background: 'transparent',
                                  border: '1px solid var(--color-border-default)',
                                  cursor: 'pointer'
                                }}
                                onClick={e => handleNotifyClick(e, card.alertId)}
                              >
                                {card.emailList?.length}{' '}
                                {card.emailList?.length === 1 ? 'Email' : 'Emails'}
                              </div>
                              <Popover
                                id={card.alertId}
                                open={selectedAlertId === card.alertId && anchorEl !== null}
                                anchorEl={anchorEl}
                                onClose={handleNotifyClose}
                                anchorOrigin={{
                                  vertical: 'bottom',
                                  horizontal: 'center'
                                }}
                                transformOrigin={{
                                  vertical: 'top',
                                  horizontal: 'center'
                                }}
                                slotProps={{
                                  paper: {
                                    style: {
                                      backgroundColor: '#121926',
                                      margin: 10
                                    }
                                  }
                                }}
                              >
                                <div style={{ padding: 14, width: 375 }}>
                                  <Typography style={{ paddingBottom: 10, fontWeight: 600 }}>
                                    People notified by this alert
                                  </Typography>
                                  {card.emailList?.map((email, index) => {
                                    return <p key={index} className="m-text">{email}</p>;
                                  })}
                                </div>
                              </Popover>
                            </TableCell>
                          }
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              );
            }
          )}
      </>
    );
  } 

  return (
    mediumDevice 
      ? loading 
        ? <Card
            variant="outlined"
            sx={{
              backgroundColor: 'var(--neutral-2)',
              borderColor: 'var(--color-border)'
            }}
          >
            <Box
                sx={{
                  display: 'flex',
                  alignItems: "flex-start",
                  justifyContent: 'space-between',
                  marginBottom: '20px'
                }}
              >
                <CardHeader
                  subheader={
                    <Skeleton
                      height={24}
                      width={150}
                      variant="rounded"
                      sx={{
                        marginTop: "10px",
                        backgroundColor: 'var(--neutral-7)',
                        border: '1px solid var(--color-border)'
                      }}
                    />
                  }
                  title={
                    <Skeleton
                      height={24}
                      width={100}
                      variant="rounded"
                      sx={{
                        backgroundColor: 'var(--neutral-7)',
                        border: '1px solid var(--color-border)'
                      }}
                    />
                  }
                />
                <CardHeader
                  style={{ width: '100%' }}
                  align="right"
                  title={
                    <Skeleton
                      height={24}
                      width={'20%'}
                      variant="rounded"
                      sx={{
                        backgroundColor: 'var(--neutral-7)',
                        border: '1px solid var(--color-border)'
                      }}
                    />
                  }
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <CardHeader
                  style={{
                    marginLeft: 16,
                    paddingLeft: 0,
                    borderBottom: '1px solid var(--color-border)'
                  }}
                  title={
                    <Skeleton
                      height={24}
                      width={100}
                      variant="rounded"
                      sx={{
                        backgroundColor: 'var(--neutral-7)',
                        border: '1px solid var(--color-border)'
                      }}
                    />
                  }
                />
                <CardHeader
                  style={{
                    marginRight: 16,
                    paddingRight: 0,
                    borderBottom: '1px solid var(--color-border)',
                    width: '100%'
                  }}
                  align="right"
                  title={
                    <Skeleton
                      height={24}
                      width={'30%'}
                      variant="rounded"
                      sx={{
                        backgroundColor: 'var(--neutral-7)',
                        border: '1px solid var(--color-border)'
                      }}
                    />
                  }
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <CardHeader
                  style={{
                    marginLeft: 16,
                    paddingLeft: 0,
                    borderBottom: '1px solid var(--color-border)'
                  }}
                  title={
                    <Skeleton
                      height={24}
                      width={100}
                      variant="rounded"
                      sx={{
                        backgroundColor: 'var(--neutral-7)',
                        border: '1px solid var(--color-border)'
                      }}
                    />
                  }
                />
                <CardHeader
                  style={{
                    marginRight: 16,
                    paddingRight: 0,
                    borderBottom: '1px solid var(--color-border)',
                    width: '100%'
                  }}
                  align="right"
                  title={
                    <Skeleton
                      height={24}
                      width={'30%'}
                      variant="rounded"
                      sx={{
                        backgroundColor: 'var(--neutral-7)',
                        border: '1px solid var(--color-border)'
                      }}
                    />
                  }
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <CardHeader
                  style={{
                    marginLeft: 16,
                    paddingLeft: 0,
                  }}
                  title={
                    <Skeleton
                      height={24}
                      width={100}
                      variant="rounded"
                      sx={{
                        backgroundColor: 'var(--neutral-7)',
                        border: '1px solid var(--color-border)'
                      }}
                    />
                  }
                />
                <CardHeader
                  style={{
                    marginRight: 16,
                    paddingRight: 0,
                    width: '100%'
                  }}
                  align="right"
                  title={
                    <Skeleton
                      height={24}
                      width={'30%'}
                      variant="rounded"
                      sx={{
                        backgroundColor: 'var(--neutral-7)',
                        border: '1px solid var(--color-border)'
                      }}
                    />
                  }
                />
              </Box>
          </Card>
        : rows.length > 0 
          ? <Card 
              ref={topElementRef}
              style={{
                backgroundColor: 'var(--neutral-3)',
                padding: '0 var(--space-m) var(--space-m) var(--space-m)'
              }}>
              <Box sx={{marginTop: "var(--space-l)"}}>
                <TableSortControls
                  selectorValues={{     
                    clear: t("clear"),
                    subAccountName: t("subAccount")
                  }}
                  onChange={handleMobileUsersCardsSort}
                  defaultSort={'subAccountName'}
                  defaultOrder={'asc'}
                />
              </Box>
              <Box paddingX={2} pt={2}>
                <Box
                  display="flex"
                  justifyContent={'flex-end'}
                  alignItems="center"
                >
                  <Typography align="right">
                    Total Items: {rows.length}
                  </Typography>
                </Box>
              </Box>
              <div className={classes.scrollButtonsContainer}>
                <button
                  onClick={handleScrollTop}
                  className={
                    topIsVisible
                      ? classes.hideScrollButton
                      : classes.topScrollButton
                  }
                >
                  <ScrollArrowsUp color={'white'} />
                </button>
                <button
                  onClick={handleScrollBottom}
                  className={
                    bottomIsVisible
                      ? classes.hideScrollButton
                      : classes.bottomScrollButton
                  }
                >
                  <ScrollArrowsDown color={'white'} />
                </button>
                {
                  renderCards()
                }
              </div>

              <ConfirmationDialog
                open={deleteConfirmDialogOpen}
                onClose={handleDeleteConfirmDialogClose}
                onConfirm={handleDeleteAlert}
                title={t('areYouSure')}
                content={t('sureToDeleteSubAccountAlert')}
                confirmLabel={t('deleteAlert')}
                cancelLabel={t('cancel')}
              ></ConfirmationDialog>

              <Dialog
                PaperProps={{
                  sx: { backgroundColor: '#0D121C', borderRadius: '12px' }
                }}
                open={editAlertModalOpen}
                onClose={handleEditModalClose}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle
                  component={'div'}
                  style={{ display: 'flex', flexDirection: 'column' }}
                >
                  <Box>
                    <div
                      style={{
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div
                        style={{
                          marginTop: 8,
                          width: 48,
                          height: 48,
                          border: '1px solid #364152',
                          borderRadius: 'var(--radius-l)',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}
                      >
                        <SquarePen color={'#CDD5DF'} />
                      </div>
                      <div
                        style={{
                          display: editAlertLoading ? 'none' : 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          marginTop: '8px',
                          position: 'relative',
                          zIndex: 3,
                          cursor: 'pointer'
                        }}
                        onClick={handleEditModalClose}
                      >
                        <X size={30} />
                      </div>
                    </div>
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                      }}
                    >
                      <svg
                        width="240"
                        height="240"
                        viewBox="0 0 240 240"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle cx="48" cy="48" r="48" stroke="#202939" />
                        <circle cx="48" cy="48" r="80" stroke="#202939" />
                        <circle
                          cx="48"
                          cy="48"
                          r="112"
                          stroke="#202939"
                          opacity={0.6}
                        />
                        <circle
                          cx="48"
                          cy="48"
                          r="144"
                          stroke="#202939"
                          opacity={0.4}
                        />
                        <circle
                          cx="48"
                          cy="48"
                          r="176"
                          stroke="#202939"
                          opacity={0.2}
                        />
                      </svg>
                    </div>
                    <Typography
                      style={{
                        marginTop: '24px',
                        fontSize: '18px',
                        fontWeight: '600',
                        lineHeight: '28px',
                        position: 'relative'
                      }}
                    >
                      {t('editAlert')}
                    </Typography>
                    <Typography
                      style={{
                        fontSize: '14px',
                        fontWeight: '400',
                        color: '#94969C',
                        position: 'relative'
                      }}
                    >
                      {t('editAlertHelperText')}
                    </Typography>
                  </Box>
                </DialogTitle>
                <DialogContent
                  style={{
                    backgroundColor: '#0D121C',
                    paddingLeft: theme.spacing(3),
                    paddingRight: theme.spacing(3),
                    paddingBottom: 0
                  }}
                >
                  <FormGroup>
                    <InputLabel className={classes.selectLabel}>
                      {t('group')}
                    </InputLabel>
                    <TextField
                      disabled
                      size="large"
                      fullWidth
                      select
                      SelectProps={{
                        IconComponent: props => (
                          <ChevronDown style={{ top: 'unset' }} {...props} />
                        ),
                        native: false,
                        MenuProps: {
                          PopoverClasses: {
                            paper: classes.tagDropDown,
                            classes: { icon: { top: '0' } }
                          }
                        }
                      }}
                      value={selectedAlertGroup}
                      variant="outlined"
                      className={classes.selectField}
                    >
                      <MenuItem value={selectedAlertGroup}>
                        {selectedAlertGroup}
                      </MenuItem>
                    </TextField>
                  </FormGroup>
                  <FormGroup>
                    <InputLabel className={classes.selectLabel}>
                      {t('subAccount')}
                    </InputLabel>
                    <TextField
                      disabled
                      size="large"
                      fullWidth
                      select
                      SelectProps={{
                        IconComponent: props => (
                          <ChevronDown style={{ top: 'unset' }} {...props} />
                        ),
                        native: false,
                        MenuProps: {
                          PopoverClasses: {
                            paper: classes.tagDropDown,
                            classes: { icon: { top: '0' } }
                          }
                        }
                      }}
                      value={selectedSubAccount}
                      variant="outlined"
                      className={classes.selectField}
                    >
                      <MenuItem value={selectedSubAccount}>
                        {selectedSubAccount}
                      </MenuItem>
                    </TextField>
                  </FormGroup>
                  <FormGroup>
                    <InputLabel className={classes.selectLabel}>
                      {t('hashrateThreshold')}
                    </InputLabel>
                    <Box style={{ display: 'flex' }}>
                      <TextField
                        onChange={handleHashrateThresholdChange}
                        value={hashrateThreshold}
                        className={classes.splitFieldLeft}
                        disabled={editAlertLoading}
                      />
                      <TextField
                        disabled={editAlertLoading}
                        size="large"
                        onChange={handleHashrateUnitChange}
                        select
                        SelectProps={{
                          IconComponent: props => (
                            <ChevronDown style={{ top: 'unset' }} {...props} />
                          ),
                          native: false,
                          MenuProps: {
                            PopoverClasses: {
                              paper: classes.tagDropDown,
                              classes: { icon: { top: '0' } }
                            }
                          }
                        }}
                        value={hashrateUnit}
                        className={classes.splitFieldRight}
                      >
                        {hashrateUnitOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {t(option.label)}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  </FormGroup>
                  <FormGroup>
                    <InputLabel className={classes.selectLabel}>
                      {t('activeWorkersThreshold')}
                    </InputLabel>
                    <TextField
                      disabled={editAlertLoading}
                      size="large"
                      onChange={handleActiveWorkersThresholdChange}
                      fullWidth
                      SelectProps={{
                        IconComponent: props => (
                          <ChevronDown style={{ top: 'unset' }} {...props} />
                        ),
                        native: false,
                        MenuProps: {
                          PopoverClasses: {
                            paper: classes.tagDropDown,
                            classes: { icon: { top: '0' } }
                          }
                        }
                      }}
                      value={activeWorkersThreshold}
                      variant="outlined"
                      className={classes.selectField}
                    />
                  </FormGroup>
                  <FormGroup>
                    <InputLabel className={classes.selectLabel}>
                      {t('emailListAlertNotification')}
                    </InputLabel>
                    <EmailChipsInput
                      handleSelectedEmails={handleSelectedEmails}
                      fullWidth
                      disabled={editAlertLoading}
                      value={emailsTextArea}
                      setEmailListError={setEmailListError}
                      selectedEmails={emailList}
                    />
                  </FormGroup>
                </DialogContent>
                <DialogActions
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: theme.spacing(4),
                    marginBottom: theme.spacing(3),
                    paddingLeft: theme.spacing(3),
                    paddingRight: theme.spacing(3)
                  }}
                >
                  <Button
                    disabled={editAlertLoading}
                    variant="outlined"
                    onClick={handleEditModalClose}
                    className={classes.buttonBase}
                  >
                    {editAlertLoading ? <Skeleton width={100} /> : t('cancel')}
                  </Button>
                  <Button
                    disabled={editAlertLoading || emailListError}
                    variant="outlined"
                    onClick={handleEditAlert}
                    className={classes.buttonBase}
                    style={
                      editAlertLoading || emailListError
                        ? {
                            border:
                              '1px solid var(--Component-colors-Components-Buttons-Primary-button-primary-border, #00C067)',
                            background:
                              'var(--Component-colors-Components-Buttons-Primary-button-primary-bg, #00C067)',
                            boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
                            color: '#FFF',
                            opacity: 0.2
                          }
                        : {
                            border:
                              '1px solid var(--Component-colors-Components-Buttons-Primary-button-primary-border, #00C067)',
                            background:
                              'var(--Component-colors-Components-Buttons-Primary-button-primary-bg, #00C067)',
                            boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
                            color: '#FFF'
                          }
                    }
                  >
                    {editAlertLoading ? (
                      <Skeleton style={{ backgroundColor: '#FFF' }} width={100} />
                    ) : (
                      t('save')
                    )}
                  </Button>
                </DialogActions>
              </Dialog>
            </Card>
          : <Box
              display="flex"
              flexDirection="column"
              justifyItems="center"
              alignItems="center"
              style={{ background: 'var(--color-background)'}}
            >
              <div style={{ 
                position: 'relative', 
                width: "100%",     
                display: "flex",
                justifyContent: "center",
                alignItems: "center"}}>
                <p
                  style={{
                    position: 'absolute',
                    top: 280,
                    fontFamily: 'var(--font-family)',
                    fontSize: 16,
                    fontWeight: 600,
                    color: "#e6e5e8"
                  }}
                >
                  Track your hashrate
                </p>
                <p
                  style={{
                    position: 'absolute',
                    top: 310,
                    fontFamily: 'var(--font-family)',
                    fontSize: 14,
                    fontWeight: 400,
                    color: '#9AA4B2'
                  }}
                >
                  Set up an alert to stay up to date on your workers.
                </p>
                <div
                  style={{
                    position: 'absolute',
                    width: 48,
                    height: 48,
                    top: 217,
                    border: '1px solid #364152',
                    borderRadius: 'var(--radius-l)'
                  }}
                ></div>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ position: 'absolute', top: 230 }}
                >
                  <path
                    d="M22 12H18L15 21L9 3L6 12H2"
                    stroke="#CDD5DF"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <svg
                  width="480"
                  height="480"
                  viewBox="0 0 480 480"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="240" cy="240" r="47.5" stroke="#202939" />
                  <circle cx="240" cy="240" r="79.5" stroke="#202939" />
                  <circle cx="240" cy="240" r="111.5" stroke="#202939" />
                  <circle
                    cx="240"
                    cy="240"
                    r="143.5"
                    stroke="#202939"
                    opacity={0.5}
                  />
                  <circle
                    cx="240"
                    cy="240"
                    r="143.5"
                    stroke="#202939"
                    opacity={0.4}
                  />
                  <circle
                    cx="240"
                    cy="240"
                    r="175.5"
                    stroke="#202939"
                    opacity={0.3}
                  />
                </svg>
              </div>
              {
                !rows.length && <div style={{position: "relative", top: "-96px", display: "flex", flexDirection: "column"}}>
                  <Button
                    style={{marginBottom: "16px"}}
                    className={classes.alertsBtn}
                    component={RouterLink}
                    variant='outlined'
                    to='/app/alerts/active'
                    startIcon={<ChartBarBig />}
                  >
                      {t('viewAlerts')}
                  </Button> 
                  <CreateAlertButton coin={coin} update={update} setUpdate={setUpdate}/>
                </div>
              }
           </Box>
      : <Card
            style={{
              backgroundColor: 'var(--uishell-color-surface)',
              boxShadow: 'none',
              marginBottom: 20
            }}
          >
      {loading || rows.length > 0 || (!loading && error) ? (
        <TableContainer
          component={Paper}
          style={{
            backgroundColor: 'var(--uishell-color-surface)',
            borderTopRightRadius: 0,
            borderTopLeftRadius: 0,
            borderTop: '2px solid var(--color-border)',
            position: 'relative',
            maxHeight: 'calc(var(--space-xl) * 20)',
            scrollbarWidth: loading ? 'none' : 'auto'
          }}
        >
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow
                className={classes.metalTableHeaderRow}
                style={{ whiteSpace: 'nowrap' }}
              >
                <TableCell align="left">
                  <TableSortLabel
                    active={true}
                    direction={subAccountOrder}
                    onClick={() => handleSubAccountColumnSort()}
                  >
                    {t('subAccount')}
                  </TableSortLabel>
                </TableCell>

                {loggedInUser?.groups?.length > 1 && (
                  <TableCell align={'left'}>{t('groupName')}</TableCell>
                )}

                <TableCell align={'left'}>{t('alertType')}</TableCell>

                <TableCell align={'left'}>{t('threshold')}</TableCell>

                <TableCell align={'left'}>{t('notifying')}</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>

            {rows.length > 0 && !loading && !error ? (
              renderAlertsTable()
            ) : !loading && error ? (
              <TableBody>
                <TableRow style={{ height: emptyRows * 5 }}>
                  <TableCell colSpan={6} align="center">
                    {' '}
                    {error}
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              renderTableSkeleton()
            )}

            <TableFooter style={{ position: 'sticky', bottom: 0 }}>
              <TableRow>
                <TablePaginationComp
                  filteredRows={rows}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  handleChangePage={handleChangePage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                  disablePagination={loading}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          justifyItems="center"
          alignItems="center"
          style={{ background: 'var(--color-background)' }}
        >
          <div style={{ position: 'relative' }}>
            <p
              style={{
                position: 'absolute',
                left: 165,
                top: 280,
                fontFamily: 'var(--font-family)',
                fontSize: 16,
                fontWeight: 600
              }}
            >
              Track your hashrate
            </p>
            <p
              style={{
                position: 'absolute',
                left: 80,
                top: 310,
                fontFamily: 'var(--font-family)',
                fontSize: 14,
                fontWeight: 400,
                color: '#9AA4B2'
              }}
            >
              Set up an alert to stay up to date on your workers.
            </p>
            <div
              style={{
                position: 'absolute',
                width: 48,
                height: 48,
                left: 217,
                top: 217,
                border: '1px solid #364152',
                borderRadius: 'var(--radius-l)'
              }}
            ></div>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ position: 'absolute', left: 230, top: 230 }}
            >
              <path
                d="M22 12H18L15 21L9 3L6 12H2"
                stroke="#CDD5DF"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <svg
              width="480"
              height="480"
              viewBox="0 0 480 480"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="240" cy="240" r="47.5" stroke="#202939" />
              <circle cx="240" cy="240" r="79.5" stroke="#202939" />
              <circle cx="240" cy="240" r="111.5" stroke="#202939" />
              <circle
                cx="240"
                cy="240"
                r="143.5"
                stroke="#202939"
                opacity={0.5}
              />
              <circle
                cx="240"
                cy="240"
                r="143.5"
                stroke="#202939"
                opacity={0.4}
              />
              <circle
                cx="240"
                cy="240"
                r="175.5"
                stroke="#202939"
                opacity={0.3}
              />
              <circle
                cx="240"
                cy="240"
                r="207.5"
                stroke="#202939"
                opacity={0.2}
              />
              <circle
                cx="240"
                cy="240"
                r="239.5"
                stroke="#202939"
                opacity={0.1}
              />
            </svg>
          </div>
          {
            !rows.length && <div style={{position: "relative", top: "-80px"}}>
              <Button
                className={classes.alertsBtn}
                component={RouterLink}
                variant='outlined'
                to='/app/alerts/active'
                startIcon={<ChartBarBig />}
              >
                  {t('viewAlerts')}
              </Button> 
              <CreateAlertButton coin={coin} update={update} setUpdate={setUpdate}/>
            </div>
          }
        </Box>
      )}

      <ConfirmationDialog
        open={deleteConfirmDialogOpen}
        onClose={handleDeleteConfirmDialogClose}
        onConfirm={handleDeleteAlert}
        title={t('areYouSure')}
        content={t('sureToDeleteSubAccountAlert')}
        confirmLabel={t('deleteAlert')}
        cancelLabel={t('cancel')}
      ></ConfirmationDialog>

      <Dialog
        PaperProps={{
          sx: { backgroundColor: '#0D121C', borderRadius: '12px' }
        }}
        open={editAlertModalOpen}
        onClose={handleEditModalClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          component={'div'}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <Box>
            <div
              style={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div
                style={{
                  marginTop: 8,
                  width: 48,
                  height: 48,
                  border: '1px solid #364152',
                  borderRadius: 'var(--radius-l)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <SquarePen color={'#CDD5DF'} />
              </div>
              <div
                style={{
                  display: editAlertLoading ? 'none' : 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginTop: '8px',
                  position: 'relative',
                  zIndex: 3,
                  cursor: 'pointer'
                }}
                onClick={handleEditModalClose}
              >
                <X size={30} />
              </div>
            </div>
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
              }}
            >
              <svg
                width="240"
                height="240"
                viewBox="0 0 240 240"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="48" cy="48" r="48" stroke="#202939" />
                <circle cx="48" cy="48" r="80" stroke="#202939" />
                <circle
                  cx="48"
                  cy="48"
                  r="112"
                  stroke="#202939"
                  opacity={0.6}
                />
                <circle
                  cx="48"
                  cy="48"
                  r="144"
                  stroke="#202939"
                  opacity={0.4}
                />
                <circle
                  cx="48"
                  cy="48"
                  r="176"
                  stroke="#202939"
                  opacity={0.2}
                />
              </svg>
            </div>
            <Typography
              style={{
                marginTop: '24px',
                fontSize: '18px',
                fontWeight: '600',
                lineHeight: '28px',
                position: 'relative'
              }}
            >
              {t('editAlert')}
            </Typography>
            <Typography
              style={{
                fontSize: '14px',
                fontWeight: '400',
                color: '#94969C',
                position: 'relative'
              }}
            >
              {t('editAlertHelperText')}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent
          style={{
            backgroundColor: '#0D121C',
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
            paddingBottom: 0
          }}
        >
          <FormGroup>
            <InputLabel className={classes.selectLabel}>
              {t('group')}
            </InputLabel>
            <TextField
              disabled
              size="large"
              fullWidth
              select
              SelectProps={{
                IconComponent: props => (
                  <ChevronDown style={{ top: 'unset' }} {...props} />
                ),
                native: false,
                MenuProps: {
                  PopoverClasses: {
                    paper: classes.tagDropDown,
                    classes: { icon: { top: '0' } }
                  }
                }
              }}
              value={selectedAlertGroup}
              variant="outlined"
              className={classes.selectField}
            >
              <MenuItem value={selectedAlertGroup}>
                {selectedAlertGroup}
              </MenuItem>
            </TextField>
          </FormGroup>
          <FormGroup>
            <InputLabel className={classes.selectLabel}>
              {t('subAccount')}
            </InputLabel>
            <TextField
              disabled
              size="large"
              fullWidth
              select
              SelectProps={{
                IconComponent: props => (
                  <ChevronDown style={{ top: 'unset' }} {...props} />
                ),
                native: false,
                MenuProps: {
                  PopoverClasses: {
                    paper: classes.tagDropDown,
                    classes: { icon: { top: '0' } }
                  }
                }
              }}
              value={selectedSubAccount}
              variant="outlined"
              className={classes.selectField}
            >
              <MenuItem value={selectedSubAccount}>
                {selectedSubAccount}
              </MenuItem>
            </TextField>
          </FormGroup>
          <FormGroup>
            <InputLabel className={classes.selectLabel}>
              {t('hashrateThreshold')}
            </InputLabel>
            <Box style={{ display: 'flex' }}>
              <TextField
                onChange={handleHashrateThresholdChange}
                value={hashrateThreshold}
                className={classes.splitFieldLeft}
                disabled={editAlertLoading}
              />
              <TextField
                disabled={editAlertLoading}
                size="large"
                onChange={handleHashrateUnitChange}
                select
                SelectProps={{
                  IconComponent: props => (
                    <ChevronDown style={{ top: 'unset' }} {...props} />
                  ),
                  native: false,
                  MenuProps: {
                    PopoverClasses: {
                      paper: classes.tagDropDown,
                      classes: { icon: { top: '0' } }
                    }
                  }
                }}
                value={hashrateUnit}
                className={classes.splitFieldRight}
              >
                {hashrateUnitOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </FormGroup>
          <FormGroup>
            <InputLabel className={classes.selectLabel}>
              {t('activeWorkersThreshold')}
            </InputLabel>
            <TextField
              disabled={editAlertLoading}
              size="large"
              onChange={handleActiveWorkersThresholdChange}
              fullWidth
              SelectProps={{
                IconComponent: props => (
                  <ChevronDown style={{ top: 'unset' }} {...props} />
                ),
                native: false,
                MenuProps: {
                  PopoverClasses: {
                    paper: classes.tagDropDown,
                    classes: { icon: { top: '0' } }
                  }
                }
              }}
              value={activeWorkersThreshold}
              variant="outlined"
              className={classes.selectField}
            />
          </FormGroup>
          <FormGroup>
            <InputLabel className={classes.selectLabel}>
              {t('emailListAlertNotification')}
            </InputLabel>
            <EmailChipsInput
              handleSelectedEmails={handleSelectedEmails}
              fullWidth
              disabled={editAlertLoading}
              value={emailsTextArea}
              setEmailListError={setEmailListError}
              selectedEmails={emailList}
            />
          </FormGroup>
        </DialogContent>
        <DialogActions
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: theme.spacing(4),
            marginBottom: theme.spacing(3),
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3)
          }}
        >
          <Button
            disabled={editAlertLoading}
            variant="outlined"
            onClick={handleEditModalClose}
            className={classes.buttonBase}
          >
            {editAlertLoading ? <Skeleton width={100} /> : t('cancel')}
          </Button>
          <Button
            disabled={editAlertLoading || emailListError}
            variant="outlined"
            onClick={handleEditAlert}
            className={classes.buttonBase}
            style={
              editAlertLoading || emailListError
                ? {
                    border:
                      '1px solid var(--Component-colors-Components-Buttons-Primary-button-primary-border, #00C067)',
                    background:
                      'var(--Component-colors-Components-Buttons-Primary-button-primary-bg, #00C067)',
                    boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
                    color: '#FFF',
                    opacity: 0.2
                  }
                : {
                    border:
                      '1px solid var(--Component-colors-Components-Buttons-Primary-button-primary-border, #00C067)',
                    background:
                      'var(--Component-colors-Components-Buttons-Primary-button-primary-bg, #00C067)',
                    boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
                    color: '#FFF'
                  }
            }
          >
            {editAlertLoading ? (
              <Skeleton style={{ backgroundColor: '#FFF' }} width={100} />
            ) : (
              t('save')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ManageAlertsTable;
